import { useLanguage } from "@/lib/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Car, Globe } from "lucide-react";

const languages = [
  { code: "en", label: "English", flag: "US" },
  { code: "pt", label: "Portugues", flag: "BR" },
  { code: "es", label: "Espanol", flag: "ES" },
];

export default function LanguageSelector() {
  const { changeLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSelect = (code) => {
    changeLanguage(code);
    navigate(`/${code}`);
  };

  return (
    <div
      data-testid="language-selector-page"
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
    >
      {/* Full-screen background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_boston-auto-service/artifacts/5noiz088_image.png)`,
        }}
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,18,29,0.65) 0%, rgba(12,45,72,0.80) 50%, rgba(5,18,29,0.90) 100%)' }} />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal to-transparent opacity-50" />

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8 animate-fade-in">
          <img
            src="https://customer-assets.emergentagent.com/job_boston-auto-service/artifacts/cfvpdtrh_WhatsApp%20Image%202026-05-11%20at%201.21.44%20PM.jpeg"
            alt="SousA Auto Service Inc"
            className="w-40 h-40 md:w-48 md:h-48 object-contain drop-shadow-lg rounded-full mx-auto"
          />
        </div>

        {/* Globe Icon */}
        <div className="mb-6 animate-fade-in-up opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <Globe className="w-7 h-7 text-teal mx-auto mb-3 drop-shadow" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl md:text-3xl tracking-wide text-white mb-1.5 drop-shadow-lg">
            SELECT YOUR LANGUAGE
          </h2>
          <p className="text-xs text-slate-300 font-body tracking-wide drop-shadow">
            SELECIONE SEU IDIOMA &middot; SELECCIONE SU IDIOMA
          </p>
        </div>

        {/* Language Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 mt-8">
          {languages.map((l, i) => (
            <button
              key={l.code}
              data-testid={`lang-select-${l.code}`}
              onClick={() => handleSelect(l.code)}
              className={`
                group relative px-10 py-4 border border-white/15
                backdrop-blur-md bg-black/40 hover:bg-black/50
                transition-all duration-500
                animate-fade-in-up opacity-0
                ${i === 0 ? 'delay-200' : i === 1 ? 'delay-300' : 'delay-400'}
              `}
              style={{ animationFillMode: 'forwards' }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ boxShadow: '0 0 30px rgba(212, 175, 55, 0.2) inset' }} />
              <span className="text-xl mb-1 block">{l.flag === 'US' ? '🇺🇸' : l.flag === 'BR' ? '🇧🇷' : '🇪🇸'}</span>
              <span className="font-heading text-base tracking-wider text-white group-hover:text-gold transition-colors duration-300 drop-shadow">
                {l.label}
              </span>
              <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gold group-hover:w-full transition-all duration-500" />
            </button>
          ))}
        </div>

        {/* Footer text */}
        <a href="https://www.google.com/maps/search/?api=1&query=216+Main+St%2C+North+Reading%2C+MA" target="_blank" rel="noopener noreferrer" className="mt-10 text-xs text-slate-300 tracking-widest uppercase font-body animate-fade-in opacity-0 delay-500 drop-shadow hover:text-teal transition-colors" style={{ animationFillMode: 'forwards' }}>
          216 Main St, North Reading, MA &middot; (781) 475-6126
        </a>
      </div>
    </div>
  );
}
